/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, Typography, breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useEventingMetadata } from 'bundles/unified-career-academy/contexts/EventingMetadataContext';

type Props = {
  title?: string | null;
  subtitle?: string | null;
  backgroundImage?: string | null;
  valueProps?: React.ReactNode;
  careerStats?: React.ReactNode;
  breadcrumbs?: React.ReactNode;
};

const styles = {
  section: (_: Theme, backgroundImage?: string | null, hasCareerStats?: boolean) => css`
    background-color: var(--cds-color-blue-900);
    background-image: ${backgroundImage ? `url(${backgroundImage})` : 'none'};
    background-repeat: no-repeat;
    background-size: cover;
    padding-top: var(--cds-spacing-300);
    padding-bottom: var(--cds-spacing-1000);
    position: relative;
    ${breakpoints.down('xs')} {
      padding-bottom: ${hasCareerStats ? 'var(--cds-spacing-1400)' : 'var(--cds-spacing-600)'};
    }
  `,
  title: () => css`
    padding-top: var(--cds-spacing-300);
    padding-bottom: var(--cds-spacing-300);
  `,
};

const RolePageHeroModule = ({ title, backgroundImage, subtitle, valueProps, careerStats, breadcrumbs }: Props) => {
  const eventingMetadata = useEventingMetadata();
  const theme = useTheme();

  if (!title) {
    return null;
  }

  return (
    <TrackedDiv
      trackingName="rdp_hero_section"
      trackClicks={false}
      withVisibilityTracking
      data={{ title, ...eventingMetadata }}
      css={styles.section(theme, backgroundImage, !!careerStats)}
    >
      <PageGridContainer>
        <Grid item xs={12}>
          {breadcrumbs}
          {subtitle && (
            <Typography variant="h2semibold" component="p" color="invertBody">
              {subtitle}
            </Typography>
          )}
          <Typography variant="d1semibold" css={styles.title} color="invertBody">
            {title}
          </Typography>
          {valueProps}
          {careerStats}
        </Grid>
      </PageGridContainer>
    </TrackedDiv>
  );
};

export default RolePageHeroModule;
